import { useSetRecoilState } from 'recoil';
import { useResultModal } from '@/hooks/useResultModal';
import { userAtom } from '@/state/loginState';
import { api } from '@/api/repository/axios';
import { useAuth, useIsMobile } from '@/common/hooks';
import Button from '@/components/common/elements/Button';
import Container from '@/components/common/elements/Container';
import Text from '@/components/common/elements/Text';
import StyledInputBox from '@/components/common/organism/StyledInputBox';
import { subwayLine } from '@/state/subwayLineState';
import { SignInValidate } from '@/utils/form.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import styled from '@emotion/styled';

import { isAppleSignInAvailable, startAppleSignIn } from '@/native/appleSignIn';
import { startKakaoLogin, startNaverLogin } from '@/common/socialLoginStart';

import { designSystem } from '@/styles/theme';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
    width: 100vw;
    margin-left: calc(-50vw + 50%);

    @media (max-width: 768px) {
        min-height: calc(100vh - 140px);
    }
`;

const BottomSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 40px 40px 60px;
    background: #fff;

    @media (max-width: 768px) {
        padding: 28px 20px 40px;
    }
`;

// 실제 <form> — 입력칸에서 Enter 를 눌렀을 때 브라우저가 기본 제출을 일으켜 로그인이 되게 한다
const LoginForm = styled.form`
    width: 100%;
    max-width: 400px;
    margin: 0;
`;

const FormTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
    margin-bottom: 8px;

    @media (max-width: 768px) {
        font-size: 1.25rem;
    }
`;

const FormSubtitle = styled.p`
    font-size: 0.9rem;
    color: #6b7280;
    text-align: center;
    word-break: keep-all;
    overflow-wrap: anywhere;
    margin-bottom: 32px;

    @media (max-width: 768px) {
        margin-bottom: 24px;
    }
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
`;

const StyledInput = styled.input`
    width: 100%;
    height: 52px;
    padding: 0 16px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    font-size: 16px; /* 16px 미만이면 iOS 사파리가 입력할 때 화면을 확대하고, 로그인 뒤에도 확대가 남는다 */
    transition: all 0.2s;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${designSystem.colors.primary};
        box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const LoginButton = styled.button<{ disabled?: boolean }>`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    background: ${props => props.disabled ? '#e5e7eb' : designSystem.colors.primary};
    color: ${props => props.disabled ? '#9ca3af' : '#fff'};
    transition: all 0.3s;
    margin-bottom: 16px;

    &:hover:not(:disabled) {
        background: ${props => props.disabled ? '#e5e7eb' : designSystem.colors.primaryDark};
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(90, 155, 150, 0.3);
    }
`;

const ForgotPasswordLink = styled.button`
    display: block;
    width: 100%;
    text-align: center;
    background: none;
    border: none;
    color: #6b7280;
    font-size: 13px;
    cursor: pointer;
    padding: 0;
    margin-bottom: 0;

    &:hover {
        color: ${designSystem.colors.primary};
        text-decoration: underline;
    }
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    margin: 24px 0;

    &::before, &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e5e7eb;
    }

    span {
        padding: 0 16px;
        font-size: 13px;
        color: #9ca3af;
    }
`;

const NaverLoginButton = styled.button`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    background: #03C75A;
    color: #fff;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s;

    &:hover:not(:disabled) {
        background: #02b351;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(3, 199, 90, 0.3);
    }

    &:disabled {
        background: #c9d6cf;
        color: #fff;
        cursor: not-allowed;
    }

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        display: block;
    }
`;

const KakaoLoginButton = styled.button`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    background: #FEE500;
    color: #191600;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s;

    &:hover {
        background: #f5dc00;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(254, 229, 0, 0.4);
    }

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        display: block;
    }
`;

// Apple HIG: 다른 소셜 버튼과 같은 높이·모서리, 검은 배경 + 흰 로고.
const AppleLoginButton = styled.button`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    background: #000;
    color: #fff;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s;

    &:hover:not(:disabled) {
        background: #1a1a1a;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        display: block;
    }
`;

const SignupLink = styled.div`
    text-align: center;
    font-size: 14px;
    color: #6b7280;

    button {
        background: none;
        border: none;
        color: ${designSystem.colors.primary};
        font-weight: 600;
        cursor: pointer;
        margin-left: 8px;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const ErrorAlert = styled.div`
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 16px;
    color: #dc2626;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ModalBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
`;

const ModalCard = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 28px 24px 20px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px;
    text-align: center;
`;

const ModalBody = styled.p`
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.6;
    text-align: center;
    margin: 0 0 20px;
`;

const Strong = styled.strong`
    color: ${designSystem.colors.primary};
    font-weight: 700;
`;

const ModalError = styled.div`
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 10px;
    padding: 10px 12px;
    color: #dc2626;
    font-size: 13px;
    margin-bottom: 14px;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 8px;
`;

const ModalCancelButton = styled.button`
    flex: 1;
    height: 46px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    color: #6b7280;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ModalRestoreButton = styled.button`
    flex: 1;
    height: 46px;
    border: none;
    border-radius: 10px;
    background: ${designSystem.colors.primary};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${designSystem.colors.primaryDark};
    }
`;

// 백엔드(LoginController.CODE_WITHDRAWN_PENDING)와 일치시켜야 함.
// 탈퇴 유예 기간 중 로그인 시도 시 응답에 포함되는 커스텀 코드.
const CODE_WITHDRAWN_PENDING = 4090;

interface WithdrawnPendingInfo {
    idOrEmail: string;
    withdrawnAt: string;
    restoreUntil: string;
    daysRemaining: number;
}

const Login = () => {
    const { showErrorMessage, resultModal } = useResultModal();
    const { isLoggedIn, setIsLoggedIn } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // 탈퇴 유예 기간 중 계정 복구 모달 상태. null이면 미표시.
    const [withdrawnInfo, setWithdrawnInfo] = useState<WithdrawnPendingInfo | null>(null);
    const [restoring, setRestoring] = useState(false);
    const [restoreError, setRestoreError] = useState<string | null>(null);
    // Apple 로그인 진행 중(웹 리다이렉트 준비 / iOS 네이티브 시트) — 중복 클릭 방지
    const [appleLoading, setAppleLoading] = useState(false);
    const setUser = useSetRecoilState(userAtom);
    const [subwayLines, setSubwayLines] = useRecoilState(subwayLine);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile();
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get('returnUrl');
    const from = returnUrl ? decodeURIComponent(returnUrl) : ((location.state as any)?.from || '/');

    const {
        handleSubmit,
        control,
        formState: { errors, isValid },
        watch,
    } = useForm({
        resolver: yupResolver(SignInValidate),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const watchValue = watch();

    const handleLogin = async () => {
        try {
            setLoading(true);
            setError(null);
            const response: any = await api.auth.login(watchValue);
            // 탈퇴 유예 기간(WITHDRAWN_PENDING) 응답 분기. 200 OK + 커스텀 code로 전달됨.
            if (response?.code === CODE_WITHDRAWN_PENDING && response?.result) {
                const r = response.result;
                setWithdrawnInfo({
                    idOrEmail: r.idOrEmail ?? watchValue.email,
                    withdrawnAt: r.withdrawnAt ?? '',
                    restoreUntil: r.restoreUntil ?? '',
                    daysRemaining: typeof r.daysRemaining === 'number' ? r.daysRemaining : 0,
                });
                setRestoreError(null);
                return;
            }
            setIsLoggedIn(true);
            navigate(from, { replace: true });
        } catch (err: any) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async () => {
        if (!withdrawnInfo) return;
        try {
            setRestoring(true);
            setRestoreError(null);
            // 모달에서 입력한 비밀번호가 아닌, 로그인 폼에 입력했던 비밀번호를 그대로 사용 (이미 검증된 값).
            await api.auth.restoreAccount(withdrawnInfo.idOrEmail, watchValue.password);
            setWithdrawnInfo(null);
            setIsLoggedIn(true);
            navigate(from, { replace: true });
        } catch (err: any) {
            const msg: string = err?.response?.data?.message || '복구에 실패했습니다. 잠시 후 다시 시도해주세요.';
            setRestoreError(msg);
        } finally {
            setRestoring(false);
        }
    };

    /** 입력칸에서 Enter 를 눌렀을 때의 기본 제출. 화면 이동을 막고, 버튼과 같은 조건일 때만 로그인한다. */
    const handleSubmitLogin = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isValid || loading) return;
        handleLogin();
    };

    const handleCancelRestore = () => {
        setWithdrawnInfo(null);
        setRestoreError(null);
    };

    const handleGoToSignup = () => {
        // 보던 화면(예: 공유받은 AI 대화)이 있으면 가입을 마치고 로그인한 뒤 그리로 돌아가게 넘긴다
        navigate(typeof from === 'string' && from.startsWith('/') && from !== '/' ? `/signup?returnUrl=${encodeURIComponent(from)}` : '/signup');
    };

    const handleNaverLogin = () => {
        if (!startNaverLogin()) showErrorMessage('네이버 로그인 설정이 필요합니다.');
    };

    const handleKakaoLogin = () => {
        if (!startKakaoLogin(from)) showErrorMessage('카카오 로그인 설정이 필요합니다.');
    };

    // Apple 로그인 — 웹은 Apple 페이지로 이동, iOS 앱은 네이티브 시트 결과를 콜백 화면으로 넘겨 같은 분기를 태운다.
    const handleAppleLogin = async () => {
        try {
            setAppleLoading(true);
            const result = await startAppleSignIn(from);
            if (result.status === 'native') {
                navigate('/login/callback/apple', { state: { appleLogin: result.payload } });
            }
            // 'cancelled' 는 사용자가 직접 닫은 것 → 아무 안내도 띄우지 않는다
        } catch (err) {
            console.error('Apple login error:', err);
            showErrorMessage('Apple 로그인을 시작할 수 없습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setAppleLoading(false);
        }
    };

    useEffect(() => {
        if (isLoggedIn) {
            navigate(from, { replace: true });
        }
    }, [isLoggedIn, navigate]);

    return (
        <PageContainer>
            {resultModal}

            <BottomSection>
                <LoginForm onSubmit={handleSubmitLogin}>
                    <FormTitle>로그인</FormTitle>
                    <FormSubtitle>계정에 로그인하여 서비스를 이용하세요</FormSubtitle>

                    {error && (
                        <ErrorAlert>
                            <span>로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.</span>
                        </ErrorAlert>
                    )}

                    <InputGroup>
                        <Controller
                            defaultValue=""
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <StyledInput
                                    type="email"
                                    placeholder="이메일을 입력하세요"
                                    {...field}
                                />
                            )}
                        />
                        <Controller
                            defaultValue=""
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <StyledInput
                                    type="password"
                                    placeholder="비밀번호를 입력하세요"
                                    {...field}
                                />
                            )}
                        />
                    </InputGroup>

                    <LoginButton
                        type="submit"
                        disabled={!isValid || loading}
                    >
                        {loading ? <ButtonSpinner /> : '로그인'}
                    </LoginButton>

                    <ForgotPasswordLink type="button" onClick={() => navigate('/password-reset')}>
                        비밀번호를 잊으셨나요?
                    </ForgotPasswordLink>

                    {(
                        <>
                            <Divider>
                                <span>또는</span>
                            </Divider>

                            <KakaoLoginButton type="button" onClick={handleKakaoLogin}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 4C7.03 4 3 7.13 3 11c0 2.5 1.69 4.7 4.23 5.95-.18.64-.67 2.37-.77 2.74-.12.46.17.45.36.33.15-.1 2.36-1.6 3.32-2.25.6.09 1.22.13 1.86.13 4.97 0 9-3.13 9-7C21 7.13 16.97 4 12 4z"/>
                                </svg>
                                카카오로 로그인
                            </KakaoLoginButton>

                            {/* 구버전 iOS 앱(네이티브 플러그인 없음)에서는 Apple 버튼 숨김 — 앱 업데이트 후 노출 */}
                            {isAppleSignInAvailable() && (
                            <AppleLoginButton type="button" onClick={handleAppleLogin} disabled={appleLoading}>
                                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                                </svg>
                                {appleLoading ? <ButtonSpinner /> : 'Apple로 로그인'}
                            </AppleLoginButton>
                            )}
                        </>
                    )}

                    <SignupLink>
                        아직 회원이 아니신가요?
                        <button type="button" onClick={handleGoToSignup}>회원가입</button>
                    </SignupLink>
                </LoginForm>
            </BottomSection>

            {withdrawnInfo && (
                <ModalBackdrop onClick={handleCancelRestore}>
                    <ModalCard onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>탈퇴 진행 중인 계정입니다</ModalTitle>
                        <ModalBody>
                            <Strong>{withdrawnInfo.daysRemaining}일</Strong> 뒤에는 복구할 수 없고 개인정보가 익명 처리돼요.
                            <br />
                            면허 인증, 급여 실태조사 응답, AI 질문 대화는 탈퇴 때 삭제되어 복구되지 않아요.
                            <br />
                            지금 복구하면 이 계정을 다시 쓸 수 있어요.
                        </ModalBody>
                        {restoreError && <ModalError>{restoreError}</ModalError>}
                        <ModalActions>
                            <ModalCancelButton onClick={handleCancelRestore} disabled={restoring}>
                                취소
                            </ModalCancelButton>
                            <ModalRestoreButton onClick={handleRestore} disabled={restoring}>
                                {restoring ? <ButtonSpinner /> : '복구하기'}
                            </ModalRestoreButton>
                        </ModalActions>
                    </ModalCard>
                </ModalBackdrop>
            )}
        </PageContainer>
    );
};

export default Login;
